import api from './api';
import type { PaginatedResponse } from '@/types';

export interface VehicleMake {
  id: string;
  name: string;
  code: string;
  logo_url?: string;
  country_of_origin?: string;
  category: '2w' | '4w' | 'cv' | 'other';
  /** All categories this make spans (backend `VehicleMake.categories`
   *  JSONField) — a make can be multi-category. Returned by the makes lookup
   *  endpoint (round-2 Wave C, item C2) so the Model form can request the
   *  combined segment vocabulary across all of a make's categories. */
  categories?: string[];
  is_active: boolean;
}

export interface VehicleModel {
  id: string;
  make: string;
  make_name: string;
  name: string;
  code: string;
  segment?: string;
  body_type?: string;
  seating_capacity?: number;
  is_active: boolean;
}

export interface VehicleVariant {
  id: string;
  model: string;
  model_name: string;
  make_name: string;
  name: string;
  code: string;
  fuel_type?: string;
  /** Human-readable fuel type name (e.g. "Diesel") from the FK. The variant
   *  lookup returns this alongside the `fuel_type` UUID — display/store this,
   *  not the UUID. */
  fuel_type_name?: string;
  transmission?: string;
  engine_cc?: number;
  ex_showroom_price?: number;
  is_active: boolean;
  /** B11: the insurer/GoDigit vehicle master codes this variant is mapped to
   *  (e.g. the GoDigit `vehicleMaincode`). Empty/absent means uncoded -- no
   *  insurer can price it yet, which is exactly the "No GoDigit vehicle code
   *  for …" failure this field exists to let the picker warn about upfront. */
  insurer_codes?: string[];
}

export interface VehicleTypeLookup {
  id: string;
  name: string;
  code: string;
}

export interface FuelTypeLookup {
  id: string;
  name: string;
  code: string;
}

export interface FuelType {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

export interface BodyType {
  id: string;
  name: string;
  code: string;
  category: '2w' | '4w' | 'cv';
  is_active: boolean;
}

export const vehicleService = {
  async getMakes(params?: { category?: string; search?: string }): Promise<VehicleMake[]> {
    // Use the unpaginated lookup endpoint so all makes appear in dropdowns
    // (the paginated list caps at page_size, hiding most of 700+ makes).
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.append('category', params.category);
    const response = await api.get<VehicleMake[]>(`/vehicles/makes/lookup/?${searchParams.toString()}`);
    return response.data;
  },

  async getModels(makeId?: string, params?: { search?: string }): Promise<VehicleModel[]> {
    const searchParams = new URLSearchParams();
    if (makeId) searchParams.append('make', makeId);
    void params;
    const response = await api.get<VehicleModel[]>(`/vehicles/models/lookup/?${searchParams.toString()}`);
    return response.data;
  },

  async getVariants(modelId?: string): Promise<VehicleVariant[]> {
    const searchParams = new URLSearchParams();
    if (modelId) searchParams.append('model', modelId);
    const response = await api.get<VehicleVariant[]>(`/vehicles/variants/lookup/?${searchParams.toString()}`);
    return response.data;
  },

  async getSegments(category?: string | string[]): Promise<string[]> {
    // Segment vocabulary is category-dependent (a 4w's "SUV" segment is
    // meaningless for a 2w). The endpoint accepts either a raw
    // VehicleModel.category code (e.g. "4w_private") or a canonical bucket
    // (e.g. "4w"). Passing an array of categories (round-2 Wave C, item C2 —
    // a multi-category Make) requests the de-duped UNION across all of them,
    // via repeated `category=` params. With no category, don't hit the
    // endpoint at all — there's nothing to populate.
    const categories = Array.isArray(category) ? category : category ? [category] : [];
    if (categories.length === 0) return [];
    const searchParams = new URLSearchParams();
    categories.forEach((c) => searchParams.append('category', c));
    const response = await api.get<string[]>(`/vehicles/segments/?${searchParams.toString()}`);
    return response.data;
  },

  async createMake(data: Partial<VehicleMake>): Promise<VehicleMake> {
    const response = await api.post<VehicleMake>('/vehicles/makes/', data);
    return response.data;
  },

  async createModel(data: Partial<VehicleModel>): Promise<VehicleModel> {
    const response = await api.post<VehicleModel>('/vehicles/models/', data);
    return response.data;
  },

  async createVariant(data: Partial<VehicleVariant>): Promise<VehicleVariant> {
    const response = await api.post<VehicleVariant>('/vehicles/variants/', data);
    return response.data;
  },

  async getTypesLookup(): Promise<VehicleTypeLookup[]> {
    const response = await api.get<VehicleTypeLookup[]>('/vehicles/types/lookup/');
    return response.data;
  },

  async getFuelTypesLookup(): Promise<FuelTypeLookup[]> {
    const response = await api.get<FuelTypeLookup[]>('/vehicles/fuel-types/lookup/');
    return response.data;
  },

  async getFuelTypes(): Promise<FuelType[]> {
    const response = await api.get<PaginatedResponse<FuelType>>('/vehicles/fuel-types/');
    return response.data.results || (response.data as unknown as FuelType[]);
  },

  async createFuelType(data: Partial<FuelType>): Promise<FuelType> {
    const response = await api.post<FuelType>('/vehicles/fuel-types/', data);
    return response.data;
  },

  async getBodyTypes(): Promise<BodyType[]> {
    const response = await api.get<PaginatedResponse<BodyType>>('/vehicles/body-types/');
    return response.data.results || (response.data as unknown as BodyType[]);
  },

  async createBodyType(data: Partial<BodyType>): Promise<BodyType> {
    const response = await api.post<BodyType>('/vehicles/body-types/', data);
    return response.data;
  },
};
